import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { 
  User, 
  Package, 
  ShoppingBag, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Truck,
  CreditCard,
  Calendar,
  Phone,
  Mail,
  MapPin
} from "lucide-react";

interface Order {
  id: string;
  order_number: string;
  total_amount: number;
  status: 'pending' | 'paid' | 'shipped' | 'delivered' | 'cancelled';
  customer_name: string;
  customer_phone: string;
  shipping_address: string;
  notes: string;
  created_at: string;
  order_items: OrderItem[];
}

interface OrderItem {
  id: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  products_2025_11_10_14_11: {
    name: string;
    type: 'product' | 'service';
    image_url: string;
  };
}

const UserDashboard = () => {
  const { user, signOut } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchUserOrders();
    }
  }, [user]);

  const fetchUserOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('orders_2025_11_10_14_11')
        .select(`
          id,
          order_number,
          total_amount,
          status,
          customer_name,
          customer_phone,
          shipping_address,
          notes,
          created_at,
          order_items_2025_11_10_14_11 (
            id,
            quantity,
            unit_price,
            total_price,
            products_2025_11_10_14_11 (
              name,
              type,
              image_url
            )
          )
        `)
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedOrders = data?.map(order => ({
        ...order,
        order_items: order.order_items_2025_11_10_14_11 || []
      })) || [];

      setOrders(formattedOrders);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "No se pudieron cargar tus pedidos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'paid':
        return <CreditCard className="h-4 w-4 text-blue-500" />;
      case 'shipped':
        return <Truck className="h-4 w-4 text-purple-500" />;
      case 'delivered':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      'pending': 'Pendiente de Pago',
      'paid': 'Pagado',
      'shipped': 'Pendiente de Entrega',
      'delivered': 'Entregado',
      'cancelled': 'Cancelado'
    };
    return labels[status as keyof typeof labels] || status;
  };

  const getStatusColor = (status: string) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'paid': 'bg-blue-100 text-blue-800 border-blue-200',
      'shipped': 'bg-purple-100 text-purple-800 border-purple-200',
      'delivered': 'bg-green-100 text-green-800 border-green-200',
      'cancelled': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <User className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-4">Acceso Requerido</h1>
          <p className="text-muted-foreground mb-8">
            Debes iniciar sesión para ver tu cuenta.
          </p>
          <Link to="/login">
            <Button size="lg">
              Iniciar Sesión
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Mi Cuenta</h1>
          <p className="text-muted-foreground">
            Bienvenido, {user.email}
          </p>
        </div>
        <Button variant="outline" onClick={signOut}>
          Cerrar Sesión
        </Button>
      </div>

      <Tabs defaultValue="orders" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="orders">Mis Pedidos</TabsTrigger>
          <TabsTrigger value="profile">Mi Perfil</TabsTrigger>
        </TabsList>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Mis Pedidos</h2>
            <Badge variant="secondary">
              {orders.length} {orders.length === 1 ? 'pedido' : 'pedidos'}
            </Badge>
          </div>

          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader>
                    <div className="h-4 bg-muted rounded w-1/4"></div>
                    <div className="h-3 bg-muted rounded w-1/6"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="h-20 bg-muted rounded"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : orders.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <ShoppingBag className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No tienes pedidos aún</h3>
                <p className="text-muted-foreground mb-6">
                  Explora nuestros productos y servicios para hacer tu primer pedido.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/productos">
                    <Button>
                      <Package className="mr-2 h-4 w-4" />
                      Ver Productos
                    </Button>
                  </Link>
                  <Link to="/servicios">
                    <Button variant="outline">
                      Ver Servicios
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {orders.map((order) => (
                <Card key={order.id}>
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          Pedido #{order.order_number}
                          {getStatusIcon(order.status)}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-4 mt-2">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {new Date(order.created_at).toLocaleDateString('es-CO')}
                          </span>
                          <span className="font-semibold text-primary">
                            ${order.total_amount.toFixed(2)}
                          </span>
                        </CardDescription>
                      </div>
                      <Badge className={getStatusColor(order.status)}>
                        {getStatusLabel(order.status)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Order Items */}
                    <div>
                      <h4 className="font-semibold mb-3">Productos/Servicios:</h4>
                      <div className="space-y-2">
                        {order.order_items.map((item) => (
                          <div key={item.id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                              {item.products_2025_11_10_14_11.image_url ? (
                                <img 
                                  src={item.products_2025_11_10_14_11.image_url} 
                                  alt={item.products_2025_11_10_14_11.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Package className="h-6 w-6 text-muted-foreground" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{item.products_2025_11_10_14_11.name}</p>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span>Cantidad: {item.quantity}</span>
                                <span>Precio: ${item.unit_price.toFixed(2)}</span>
                                <Badge variant="outline" className="text-xs">
                                  {item.products_2025_11_10_14_11.type === 'product' ? 'Producto' : 'Servicio'}
                                </Badge>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold">${item.total_price.toFixed(2)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Order Details */}
                    <Separator />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <h5 className="font-semibold mb-2">Información de Contacto:</h5>
                        <div className="space-y-1 text-muted-foreground">
                          <p className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            {order.customer_name}
                          </p>
                          <p className="flex items-center gap-2">
                            <Phone className="h-4 w-4" />
                            {order.customer_phone}
                          </p>
                        </div>
                      </div>
                      {order.shipping_address && (
                        <div>
                          <h5 className="font-semibold mb-2">Dirección de Envío:</h5>
                          <p className="flex items-start gap-2 text-muted-foreground">
                            <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            {order.shipping_address}
                          </p>
                        </div>
                      )}
                    </div>

                    {order.notes && (
                      <>
                        <Separator />
                        <div>
                          <h5 className="font-semibold mb-2">Notas:</h5>
                          <p className="text-muted-foreground text-sm">{order.notes}</p>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Información del Perfil</CardTitle>
              <CardDescription>
                Tu información personal y de contacto
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                  <User className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{user.email}</h3>
                  <p className="text-muted-foreground">
                    Miembro desde {new Date(user.created_at).toLocaleDateString('es-CO')}
                  </p>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3">Estadísticas</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total de pedidos:</span>
                      <span className="font-medium">{orders.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Pedidos entregados:</span>
                      <span className="font-medium">
                        {orders.filter(o => o.status === 'delivered').length}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total gastado:</span>
                      <span className="font-medium text-primary">
                        ${orders.reduce((sum, order) => sum + order.total_amount, 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-3">Acciones Rápidas</h4>
                  <div className="space-y-2">
                    <Link to="/productos" className="block">
                      <Button variant="outline" className="w-full justify-start">
                        <Package className="mr-2 h-4 w-4" />
                        Ver Productos
                      </Button>
                    </Link>
                    <Link to="/servicios" className="block">
                      <Button variant="outline" className="w-full justify-start">
                        <ShoppingBag className="mr-2 h-4 w-4" />
                        Ver Servicios
                      </Button>
                    </Link>
                    <Link to="/carrito" className="block">
                      <Button variant="outline" className="w-full justify-start">
                        <ShoppingBag className="mr-2 h-4 w-4" />
                        Ver Carrito
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UserDashboard;